// МАСТЕР РАССЫЛКИ (docs/CABINET-UX-FIXLIST.md, пакет «Рассылки», ТЗ 16).
//
// РАССЫЛКА — РАЗОВАЯ И ОДНИМ СООБЩЕНИЕМ (владелец 31.08.2026). Раздел урезан: здесь
// живёт только «отправить одно сообщение — сейчас или в назначенный день и час».
// Всё регулярное и все цепочки писем уехали в «Боты» (через бота рассылку тоже можно
// сделать) — страниц «письмо N», пауз и условий в анкете больше нет.
//
// Анкета собирает ОБЫЧНУЮ схему: «Начало рассылки» → (картинка) → «Сообщение». Ровно те
// же узлы, что кладёт руками конструктор; второго движка сообщений нет и не будет.
//
// ЧЕМ ОТЛИЧАЕТСЯ ОТ ОСТАЛЬНЫХ АНКЕТ:
//  • шаблона у неё НЕТ (standalone): патчить нечего, схема рождается целиком из ответов;
//  • правится она ТОЛЬКО собой: `schemaEditable` снят (владелец 31.08.2026 — «рассылку
//    нельзя открывать конструктором»), поэтому виды схемы у неё закрыты, как у анкет
//    шаблонов, и ручная правка её схему больше не подменяет.
//
// Отправитель — только Телеграм (решение пакета): почту и Инстаграм не берём.
import {
  chatStartJson,
  BROADCAST_TRIGGER_TYPE,
  plainToTgHtml,
  TG_MESSAGE_NODE_TYPE,
  type CatalogParam,
  type WorkflowJson,
  type WorkflowNode,
} from '@scriptera/ui/scenario';
import { letterBundleOf } from '../tg-letter-bundle';
import {
  legacyWizardToBundle,
  toTgDraft,
  type TgDraft,
  type TgMessageBundle,
} from '@scriptera/db/tg-message';
import {
  ansStr,
  wizAccountParam,
  wizNodeTitle,
  type TemplateWizardDef,
  type WizardItemRow,
  type WizardLocale,
  type WizardStep,
} from './core';

/** Слаг анкеты — им раздел «Рассылки» открывает мастер (wizardById). */
export const BROADCAST_WIZARD_ID = 'broadcast';

/** Имена ответов сообщения. Ответы анкеты плоские — своих вложенных объектов не заводим. */
export const bcastField = {
  text: 'msgText',
  photo: 'msgPhoto',
  buttons: 'msgBtns',
  /**
   * СОБРАННОЕ СООБЩЕНИЕ ЦЕЛИКОМ (MESSAGE-COMPOSER Б2) — строка JSON модели
   * `TgMessageBundle`: голосовое, кружок, альбом, кнопки, оформление. Заполняет его
   * окно «Сообщение»; пока окно не встало в анкету (Г5), поле пустое, и сообщение
   * собирается из прежней тройки «текст / картинка / кнопки» адаптером.
   */
  message: 'message',
} as const;

/** Кнопка сообщения: подпись плюс адрес (пусто — кнопка без ссылки, её нажатие мы считаем). */
export interface BroadcastButton {
  text: string;
  url: string;
}

/** Прочитанное сообщение рассылки. */
export interface BroadcastLetter {
  text: string;
  photo: string;
  buttons: BroadcastButton[];
}

/**
 * Кнопки из ответа-строки: по кнопке на строку, подпись и адрес через «|».
 * Формат тот же, что у списка вариантов ответа в других анкетах (строка на строку) —
 * второго способа хранить список у анкет не заводим.
 */
export function bcastButtons(raw: string): BroadcastButton[] {
  return raw
    .split('\n')
    .map((line) => {
      const at = line.indexOf('|');
      const text = (at === -1 ? line : line.slice(0, at)).trim().slice(0, 64);
      const url = at === -1 ? '' : line.slice(at + 1).trim().slice(0, 500);
      return { text, url: /^https?:\/\//i.test(url) ? url : '' };
    })
    .filter((b) => b.text !== '')
    .slice(0, 8);
}

/**
 * СООБЩЕНИЕ РАССЫЛКИ ОДНОЙ МОДЕЛЬЮ (MESSAGE-COMPOSER Б2). Собрано окном «Сообщение» —
 * берём его как есть; собрано прежней анкетой (текст, картинка, кнопки) — приводим
 * адаптером. Старые черновики от этого не пересобираются: их схема живёт как жила,
 * пересборка случается только при правке рассылки человеком (Е10).
 */
export function bcastBundle(answers: Record<string, unknown>): TgMessageBundle {
  // Письмо, собранное окном, лежит ЧЕРНОВИКОМ (модель TgDraft) — на сообщения его делит
  // общая letterBundleOf (lib/tg-letter-bundle.ts), а не анкета: правила деления живут в одном месте (ТЗ Б2).
  const draft = bcastDraft(answers);
  if (draft) return letterBundleOf(draft);
  const raw = ansStr(answers, bcastField.message, 200_000);
  if (raw !== '') {
    try {
      const parsed = JSON.parse(raw) as TgMessageBundle;
      if (Array.isArray(parsed?.parts) && parsed.parts.length > 0) return parsed;
    } catch {
      // Битый JSON — не повод потерять рассылку: собираем из прежних полей.
    }
  }
  const letter = bcastLetter(answers);
  const first = letter.buttons[0];
  return letterBundleOf({
    // Текст анкеты обычный, а письмо уходит с разметкой Телеграма: экранируем, иначе
    // одиночная «<» или «&» в тексте клиента валит отправку.
    ...legacyWizardToBundle({
      text: plainToTgHtml(letter.text),
      ...(letter.photo ? { image: letter.photo } : {}),
      ...(first ? { buttonText: first.text, buttonUrl: first.url } : {}),
    }),
    // Кнопок у рассылки бывает несколько — адаптер троек знает только одну, остальные
    // дописываем тем же правилом: адрес есть — ссылка, нет — метка по подписи кнопки.
    buttons: letter.buttons.map((b) => [
      b.url ? { text: b.text, kind: 'url' as const, url: b.url } : { text: b.text, kind: 'tag' as const, tag: b.text },
    ]),
  });
}

/** Письмо, собранное окном «Сообщение»; null — окно его ещё не трогало. */
export function bcastDraft(answers: Record<string, unknown>): TgDraft | null {
  const raw = ansStr(answers, bcastField.message, 200_000);
  if (raw === '') return null;
  try {
    return toTgDraft(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** В сообщении рассылки есть кнопки (APP-CLAIMS-1020 ред. 6, П20: строка отчёта «Нажали кнопку» — только тогда):
 *  письмо окна «Сообщение», а нет его — прежние поля анкеты. */
export function bcastHasButtons(answers: Record<string, unknown>): boolean {
  const d = bcastDraft(answers);
  if (d) return d.buttons.some((row) => row.length > 0);
  return bcastLetter(answers).buttons.length > 0;
}

/** Сообщение рассылки из ответов анкеты. */
export function bcastLetter(answers: Record<string, unknown>): BroadcastLetter {
  return {
    text: ansStr(answers, bcastField.text, 3000),
    photo: ansStr(answers, bcastField.photo, 500),
    buttons: bcastButtons(ansStr(answers, bcastField.buttons, 2000)),
  };
}

// ── Слова анкеты и собранной схемы ────────────────────────────────────────────────────
//
// Язык выбирается ОДИН раз, в момент создания рассылки, и запекается в черновик
// (wizard.locale): смена языка кабинета потом тексты готовой схемы не переписывает.

const TXT = {
  ru: {
    start: 'Начало рассылки',
    letter: 'Сообщение',
    noteStart:
      'Начало рассылки: получатели и время отправки задаются в мастере рассылки и в разделе «Рассылки»',
  },
  en: {
    start: 'Broadcast start',
    letter: 'Message',
    noteStart: 'Broadcast start: recipients and send time are set in the broadcast wizard and in “Broadcasts”',
  },
} as const;

/** Подписи анкеты (то, что человек читает в мастере). */
const UI = {
  ru: {
    // ЗНАКОМСТВО — первый экран анкеты (ТЗ-2 З1).
    leadTitle: 'Рассылка',
    leadText:
      'Одно сообщение отправляется вашим подписчикам в Telegram вручную или в назначенное время. Вы выбираете получателей и время, отправку выполняет бот.',
    whoTitle: 'Получатели',
    whoField: 'Кто получит',
    bot: 'Бот',
    letterTitle: 'Сообщение',
    // ПОДВАЛ ГРУППЫ, а не подсказка внутри строки (И10 п.3): прежние две фразы
    // («Напишите, что придёт человеку…» и «Соберите сообщение: текст, картинка, кнопки»)
    // объединены в одну — вторая была тем же самым другими словами.
    letterIntro:
      'Составьте сообщение: текст, фото или файл, кнопки (одна кнопка на строку).',
    text: 'Текст',
    textPh: 'Напишите сообщение',
    photo: 'Картинка',
    photoHint: 'Отправляется вместе с сообщением. Необязательно.',
    buttons: 'Кнопки',
    buttonsHint: 'Одна кнопка на строку. Чтобы добавить ссылку, укажите её после знака «|».',
    whenTitle: 'Когда',
    // ОДНА СТРОКА ВМЕСТО ДВУХ (И10 п.6): «Отправить» со значением «Вручную» (APP-CLAIMS-1020 ред. 6, П16) либо готовой
    // датой; вид выбирается листом, и «В назначенное время» сразу поднимает окна дня и
    // часа. Отдельной строки «День и час» больше нет ни в вебе, ни в приложении.
    when: 'Отправить',
    whenManual: 'Вручную',
    whenManualHint: 'Сохранённую рассылку отправляет кнопка «Отправить» — в меню карточки или на странице отчёта.',
    whenDate: 'В назначенное время',
    whenDateHint: 'Отправляется один раз в выбранное время по вашему часовому поясу',
    runAt: 'День и час',
    needText: 'Напишите текст сообщения или приложите картинку',
    letterEmpty: 'Не заполнено',
    needRunAt: 'Выберите день и час',
    needFuture: 'Указанное время уже прошло. Выберите другое.',
  },
  en: {
    leadTitle: 'Broadcast',
    leadText:
      'One message is sent to your Telegram subscribers manually or at a scheduled time. You choose the recipients and the time; the bot handles the sending.',
    whoTitle: 'Recipients',
    whoField: 'Who receives it',
    bot: 'Bot',
    letterTitle: 'Message',
    letterIntro:
      'Compose the message: text, a photo or a file, buttons (one button per line).',
    text: 'Text',
    textPh: 'Write the message',
    photo: 'Image',
    photoHint: 'Sent together with the message. Optional.',
    buttons: 'Buttons',
    buttonsHint: 'One button per line. To add a link, put it after the “|” sign.',
    whenTitle: 'When',
    when: 'Send',
    whenManual: 'Manually',
    whenManualHint: 'A saved broadcast is sent with the “Send” button — in the card menu or on the report page.',
    whenDate: 'At a set time',
    whenDateHint: 'Sent once at the selected time in your time zone',
    runAt: 'Day and hour',
    needText: 'Write the message text or attach an image',
    letterEmpty: 'Not filled in',
    needRunAt: 'Select the day and hour',
    needFuture: 'The selected time has already passed. Choose another one.',
  },
} as const;

/** Ключ страницы сообщения (по нему stepBlocked узнаёт, что сейчас на экране). */
export const BCAST_LETTER_STEP = 'letter';
/** Ключ страницы «Получатели» (бот-отправитель и «Кто получит»). */
export const BCAST_WHO_STEP = 'who';
/** Ответ с условиями отбора — строка JSON (`'{}'` = все). */
export const BCAST_AUDIENCE_PARAM = 'audience';

/**
 * «КТО ПОЛУЧИТ» В ОБЩЕМ РОДНОМ МАСТЕРЕ (владелец 12.10.2026, ТЗ BCAST-NOACC-WHO).
 * Рассылку без аккаунта в приложении ведёт общий родной мастер, а он отдал бы ответ
 * `audience` текстовым полем — человек видел ввод со скобками `{}`. Поэтому:
 * — нет аккаунта: поля нет вовсе, на шаге только «Добавить аккаунт»;
 * — аккаунт подключили (тот же экран): строка-переход «Кто получит — N», тап открывает
 *   общий экран выбора людей (openNativeRow → openAudienceScreen).
 * Веб в браузере этим не пользуется — там строку рисует свой компонент.
 */
export function bcastWhoNative(
  step: WizardStep,
  accNone: boolean,
  words: string,
): { params: CatalogParam[]; rows: WizardItemRow[] } {
  const aud = step.params.find((p) => p.name === BCAST_AUDIENCE_PARAM);
  if (step.key !== BCAST_WHO_STEP || !aud) return { params: step.params, rows: step.rows ?? [] };
  const params = step.params.filter((p) => p.name !== BCAST_AUDIENCE_PARAM);
  if (accNone) return { params, rows: step.rows ?? [] };
  return {
    params,
    rows: [
      ...(step.rows ?? []),
      {
        key: BCAST_AUDIENCE_PARAM,
        title: aud.displayName ?? BCAST_AUDIENCE_PARAM,
        value: words,
        kind: 'audience',
        param: BCAST_AUDIENCE_PARAM,
      },
    ],
  };
}

/**
 * РОДНАЯ ФОРМА РАССЫЛКИ ИЛИ ОБЩИЙ МАСТЕР (владелец 12.10.2026). Мастер, открытый без
 * аккаунта, после подключения аккаунта остаётся ТЕМ ЖЕ экраном до закрытия — в родную
 * форму не переезжает (`startedNoAcc` запоминается на всё открытие).
 */
export function bcastNativeFormOn(o: {
  edit: boolean;
  isBcast: boolean;
  ready: boolean;
  accNone: boolean;
  startedNoAcc: boolean;
}): boolean {
  return !o.edit && o.isBcast && o.ready && !o.accNone && !o.startedNoAcc;
}

type P = CatalogParam;

/**
 * Страница «Кому»: бот-отправитель и ОДНА строка «Кто получит».
 *
 * ПОДВОДКИ-ГЕРОЯ У ШАГА НЕТ (владелец 04.09.2026, И10 п.3): первая группа начинается
 * сразу под шапкой, а пояснение «Тем, кто остановил бота и попросил не писать, не пишем»
 * стоит ПОДВАЛОМ ГРУППЫ — его рисует сама строка (веб — `WizAudience`, приложение —
 * `bcast-native`), потому что в подвал же уходит и живая строка «Пока никто не получит».
 */
function whoStep(locale: WizardLocale): WizardStep {
  const t = UI[locale];
  return {
    key: BCAST_WHO_STEP,
    title: t.whoTitle,
    params: [
      { ...wizAccountParam('telegram', locale), displayName: t.bot },
      // Одна строка «Кто получит» — за ней общий экран живых людей (И3, audience-screen):
      // значение — условия отбора строкой JSON, ровно как их держит узел «Начало».
      { name: BCAST_AUDIENCE_PARAM, type: 'string', default: '{}', displayName: t.whoField },
    ],
  };
}

/** Страница сообщения: текст, картинка, кнопки. Одна на всю рассылку. */
function letterStep(locale: WizardLocale): WizardStep {
  const t = UI[locale];
  const params: P[] = [
    {
      name: bcastField.text,
      type: 'string',
      default: '',
      displayName: t.text,
      placeholder: t.textPh,
      typeOptions: { rows: 4 },
    },
    {
      name: bcastField.photo,
      type: 'string',
      default: '',
      displayName: t.photo,
      description: t.photoHint,
    },
    {
      name: bcastField.buttons,
      type: 'string',
      default: '',
      displayName: t.buttons,
      description: t.buttonsHint,
    },
  ];
  // СОБРАННОЕ ОКНОМ «СООБЩЕНИЕ» ПИСЬМО: ответ живёт строкой JSON, как `audience` держит
  // отбор аудитории. Поле само по себе не рисуется — его заполняет окно.
  params.push({
    name: bcastField.message,
    type: 'string',
    default: '',
    displayName: t.letterTitle,
    displayOptions: { show: { [bcastField.message]: ['__never__'] } },
  });
  return {
    key: BCAST_LETTER_STEP,
    title: t.letterTitle,
    // Подводки-героя у шага нет (И10 п.3): пояснение живёт ПОДВАЛОМ ГРУППЫ — оно про
    // строку «Сообщение», а не про весь экран (`hint` карточки ниже).
    params,
    // В БРАУЗЕРЕ ШАГ — ОДНА СТРОКА-КАРТОЧКА «Сообщение» (ТЗ Б, В2): письмо собирается
    // общим окном, а не тремя полями. Прежние поля остаются в ответах: ими живёт родная
    // форма рассылки, пока у неё нет родного окна (Г8), и ими же окно наполняется в
    // первый раз (адаптер Е10); с экрана их убирает `covers`.
    messages: [{
      param: bcastField.message,
      title: t.letterTitle,
      empty: t.letterEmpty,
      // СПРАВА — СОСТАВ ПИСЬМА, а не его первая строка (И11.3): «Фото, подпись,
      // кнопки (2)». Строка списка говорит, ЧТО здесь, а текст человек прочтёт в окне.
      summary: 'compose',
      hint: t.letterIntro,
      covers: [bcastField.text, bcastField.photo, bcastField.buttons],
      legacy: (answers) => {
        const letter = bcastLetter(answers);
        if (letter.text === '' && letter.photo === '' && letter.buttons.length === 0) return null;
        return {
          ...legacyWizardToBundle({
            text: plainToTgHtml(letter.text),
            ...(letter.photo ? { image: letter.photo } : {}),
          }),
          buttons: letter.buttons.map((b) => [
            b.url
              ? { text: b.text, kind: 'url' as const, url: b.url }
              : { text: b.text, kind: 'tag' as const, tag: b.text },
          ]),
        };
      },
    }],
  };
}

/**
 * Страница «Когда» — ОДНО ПОЛЕ, А НЕ ДВА (владелец 04.09.2026, И10 п.6). Прежде рядом
 * стояли «Когда отправлять» (два варианта) и появлявшаяся следом «День и час» с
 * календарём — не канон: зависимая строка вклинивалась в ту же группу.
 *
 * Теперь: одна строка «Отправить» со значением «Вручную» либо готовой датой; тап открывает
 * лист с двумя вариантами галочкой, а выбор «В назначенное время» СРАЗУ поднимает окна
 * дня и часа. Ответы (`startMode`, `runAt`, `tz`) и их чтение (`readWhen`) не меняются:
 * `runAt` остаётся ответом, но своей строки на экране у него больше нет НИГДЕ — ни в
 * вебе, ни в приложении (`__never__` гасит его в общем рендерере и в родных строках).
 * Вида «по метке» тут по-прежнему нет.
 */
function whenStep(locale: WizardLocale): WizardStep {
  const t = UI[locale];
  return {
    key: 'when',
    title: t.whenTitle,
    // Подвал группы — про ЧАСОВОЙ ПОЯС, и он живой (имя пояса и смещение берутся у
    // браузера): его рисует строка, а не деф.
    params: [
      {
        name: 'startMode',
        type: 'options',
        default: 'manual',
        displayName: t.when,
        // СВОЙ КОНТРОЛ СТРОКОЙ (И10 п.6): не раскрытый список вариантов, а одна строка со
        // значением, за которой лист выбора и окна дня и часа. Помечаем ПАРАМЕТР, а не
        // имя: `startMode` есть и у мастера запуска Инстаграма — там он про кодовые
        // слова, и трогать его нельзя.
        typeOptions: { wizControl: 'when' },
        options: [
          { name: t.whenManual, value: 'manual', description: t.whenManualHint },
          { name: t.whenDate, value: 'date', description: t.whenDateHint },
        ],
      },
      {
        name: 'runAt',
        type: 'string',
        default: '',
        displayName: t.runAt,
        // Ответ жив, строки нет: значение стоит в строке «Отправить», а календарь
        // поднимает лист вида. Тот же приём, что у собранного окном письма выше.
        displayOptions: { show: { runAt: ['__never__'] } },
      },
    ],
  };
}

// ── Сборка схемы ──────────────────────────────────────────────────────────────────────

/**
 * Шаг «Сообщение в Телеграм» — НАШ узел (MESSAGE-COMPOSER Б6), а не штатный телеграмный.
 * Причина простая: у штатного нет ни голосового, ни кружка, а вложение он берёт ссылкой
 * — кружок по ссылке площадка не принимает вовсе. Всё сообщение целиком (текст, файлы,
 * кнопки) едет одним параметром: делит его на сообщения и отдаёт площадке общий
 * отправитель, по правилам Телеграма.
 *
 * Приставки кнопок нет НАРОЧНО: нажатие кнопки рассылки без ссылки вешает человеку
 * метку по её подписи (ТЗ В4) — этим занимается приёмник бота, сценарию оно не нужно.
 */
function messageNode(id: string, name: string, bundle: TgMessageBundle, startName: string): WorkflowNode {
  return {
    id,
    name,
    type: TG_MESSAGE_NODE_TYPE,
    typeVersion: 1,
    position: [0, 0],
    parameters: {
      chatId: chatStartJson(startName, 'message.chat.id'),
      message: JSON.stringify(bundle),
    },
  };
}

/**
 * ВЫБРАННЫЙ МОМЕНТ УЖЕ В ПРОШЛОМ (И11.9 п.7). Момент хранится «стенными часами» без
 * пояса («2026-09-05T12:00») — сверяем его с часами того же человека. Кривая строка
 * прошлым не считается: про неё говорит отдельный замок «Выберите день и час».
 */
export function whenIsPast(runAt: string, now: Date = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(runAt)) return false;
  const at = new Date(runAt);
  return !Number.isNaN(at.getTime()) && at.getTime() <= now.getTime();
}

/** Прочитанные ответы «Когда». Незнакомое значение — «сразу»: сам он не стартует. */
function readWhen(answers: Record<string, unknown>): {
  mode: 'manual' | 'date';
  runAt: string;
  tz: string;
} {
  const mode = ansStr(answers, 'startMode', 8, 'manual') === 'date' ? 'date' : 'manual';
  return {
    mode,
    runAt: mode === 'date' ? ansStr(answers, 'runAt', 32) : '',
    tz: mode === 'date' ? ansStr(answers, 'tz', 64) : '',
  };
}

export const broadcastWizard: TemplateWizardDef = {
  id: BROADCAST_WIZARD_ID,
  // Шаблона нет: схему собирает сама анкета (см. standalone в core.ts).
  templateSource: '',
  standalone: true,
  // ЗНАКОМСТВА У РАССЫЛКИ НЕТ (владелец 04.09.2026, И10 п.3): «героя убрать целиком,
  // первая группа „Кому“ начинается сразу под шапкой». Человек уже нажал «Создать
  // рассылку» — экран-подводка перед короткой формой «кому / что / когда» лишний.
  noIntro: true,
  accountChannel: 'telegram',
  // ФИНАЛ ОТМЕНЁН ВЛАДЕЛЬЦЕМ 18.09.2026 (ТЗ П126.10): после создания — список «Боты»,
  // как у всех прочих путей; поле больше не читается (см. createdPath в core.ts).
  // Прежнее правило: готовая рассылка открывалась страницей цифр (/app/bots/<id>/stats),
  // где живут её кнопки «Отправить» и «Остановить». Теперь эта страница открывается
  // тапом по карточке рассылки в списке.
  createdPath: (draftId) => `/app/bots/${draftId}/stats`,

  // ЗНАКОМСТВО — первый экран анкеты (ТЗ-2 З1).
  // Сцены с примеров работ у рассылки нет: там три сюжета про ботов, а рассылку человек
  // отправляет сам (ТЗ-3 Т1) — первый экран остаётся именем и описанием.
  demoScene: null,

  lead(locale) {
    const t = UI[locale];
    return { title: t.leadTitle, text: t.leadText };
  },

  steps(locale) {
    return [whoStep(locale), letterStep(locale), whenStep(locale)];
  },

  // Свой инвариант страницы: пустое сообщение required-полями не выразить (текста может
  // не быть, если есть картинка), а день и час обязательны только у вида «в дату».
  stepBlocked(answers, stepKey, locale) {
    const t = UI[locale];
    if (stepKey === BCAST_LETTER_STEP) {
      // Письмо, собранное окном «Сообщение», тоже считается заполненным: у него может не
      // быть текста вовсе (голосовое, кружок, альбом).
      const draft = bcastDraft(answers);
      if (draft && (draft.text.trim() !== '' || draft.media.length > 0)) return null;
      const letter = bcastLetter(answers);
      return letter.text === '' && letter.photo === '' ? t.needText : null;
    }
    if (stepKey === 'when') {
      const when = readWhen(answers);
      if (when.mode === 'date') {
        if (when.runAt === '') return t.needRunAt;
        // ВРЕМЯ УЖЕ ПРОШЛО (И11.9 п.7): раньше проверки не было вовсе — рассылка
        // создавалась с датой в прошлом и не уходила никогда. Момент хранится
        // «стенными часами» браузера, ими же и сверяем.
        if (whenIsPast(when.runAt)) return t.needFuture;
      }
    }
    return null;
  },

  /**
   * СБОРКА СХЕМЫ. Канон приходит пустым (standalone) — берём из него только служебные
   * поля (settings и прочее, что положил emptyWorkflow) и кладём свою цепочку:
   * «Начало рассылки» → (картинка) → «Сообщение».
   */
  buildWorkflow(template, answers, locale) {
    const T = TXT[locale];
    const letter = bcastLetter(answers);
    const when = readWhen(answers);
    const nodes: WorkflowNode[] = [];
    const connections: WorkflowJson['connections'] = {};
    const taken = new Set<string>();

    const startName = wizNodeTitle(T.start, taken, T.start);
    nodes.push({
      id: 'bcast-start',
      name: startName,
      type: BROADCAST_TRIGGER_TYPE,
      typeVersion: 1,
      position: [0, 0],
      parameters: {
        mode: when.mode,
        ...(when.mode === 'date' ? { runAt: when.runAt, tz: when.tz } : {}),
        // Условия отбора — снимок выбора из кабинета; разбирает их наш код
        // (@scriptera/db normalizeAudience), поэтому лишнего не проверяем.
        audience: ansStr(answers, 'audience', 4000, '{}'),
      },
      notes: T.noteStart,
    });

    const link = (from: string, to: string): void => {
      connections[from] = { main: [[{ node: to, type: 'main', index: 0 }]] } as (typeof connections)[string];
    };

    // Картинка больше НЕ отдельным шагом: она часть сообщения, и делит письмо на
    // сообщения общий отправитель (подпись влезла — уйдёт одним, не влезла — двумя).
    const msgName = wizNodeTitle(letter.text, taken, T.letter);
    nodes.push({ ...messageNode('bcast-msg', msgName, bcastBundle(answers), startName), position: [260, 0] });
    link(startName, msgName);

    // Имя рассылки — по её сообщению (как имена шагов): человек узнаёт её в списке по
    // тому, что в ней написано. Пустой текст (только картинка) — общее слово.
    // При ПРАВКЕ имя перекрывает сервер текущим названием черновика (переименовать
    // рассылку можно в «О сценарии», и мастер это не отменяет).
    const name = wizNodeTitle(letter.text, new Set(), T.letter);
    return { ...template, name, nodes, connections };
  },
};
